import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";

import { dutyActivityReportCopy, type DutyActivityMediaCounts, type DutyActivityReport } from "../data/duty-activity-report";
import type { ServiceWeekCheckpointRow } from "../data/service-week-approval";
import { paginateArtifactRows, type ArtifactPage } from "../lib/client-artifact-pages";
import { serviceCheckpointCopy } from "../lib/service-week";
import {
  ClientArtifactFooter, ClientArtifactHeader, ClientArtifactPage, clientArtifactInstant as instant,
} from "./ClientArtifactPage";
import "../styles/proof-of-service.css";
import "../styles/duty-activity-report.css";

type DutySection = "incidents" | "checkpoints";
const sections: DutySection[] = ["incidents", "checkpoints"];
const sectionNames: Record<DutySection, string> = { incidents: "Incidents", checkpoints: "Checkpoint exceptions" };
const checkpointHeadings = ["Route", "Checkpoint", "Recorded", "Exception noted"];
const incidentHeadings = ["Occurred", "Post / site", "Category", "Severity", "Review state", "Attribution"];
const category = { safety: "Safety", security: "Security", maintenance: "Maintenance", client_note: "Client note", other: "Other" };
const severity = { info: "Information", low: "Low", medium: "Medium", high: "High", critical: "Critical" };
const reviewState = { submitted: "Submitted", under_review: "Under review", locked: "Locked" };
// P-15: plain client English for how the week claimed this incident. Never the stored token.
const attribution = { assignment_linked: "On a scheduled assignment", unlinked_by_occurred_at: "By time of occurrence" };
const mediaWords: Array<[keyof DutyActivityMediaCounts, string, string]> = [
  ["photo", "photo", "photos"], ["video", "video", "videos"], ["audio", "audio file", "audio files"],
  ["document", "document", "documents"], ["signature", "signature", "signatures"],
];

type SheetRow =
  | { section: "incidents"; incident: DutyActivityReport["incidents"][number] | null }
  | { section: "checkpoints"; checkpoint: ServiceWeekCheckpointRow | null };

function mediaSentence(counts: DutyActivityMediaCounts): string {
  const parts = mediaWords
    .filter(([kind]) => counts[kind] > 0)
    .map(([kind, one, many]) => `${counts[kind]} ${counts[kind] === 1 ? one : many}`);
  return parts.length === 0 ? "None" : parts.join(", ");
}

function Summary({ report }: { report: DutyActivityReport }) {
  const s = report.coverageSummary;
  return <dl className="pos-paper-summary pos-paper-summary--dar">
    <div><dt>Incidents</dt><dd>{s.incidentCount}</dd></div>
    <div><dt>Missed check-ins</dt><dd>{s.missedCheckInCount}</dd></div>
    <div><dt>Checkpoint exceptions</dt><dd>{s.checkpointExceptionCount}</dd></div>
    <div><dt>Check-ins device-verified</dt><dd>{s.verifiedCheckInCount} of {s.expectedCheckInCount}</dd></div>
    <div><dt>Scheduled seats with recorded service</dt><dd>{s.coveredSeatCount} of {s.publishedSeatCount}</dd></div>
    {/* D-06 names these two separately. A client should not have to subtract to
        find an uncovered seat, and a missing shift boundary is exactly the fact a
        client artifact must not be able to omit quietly. */}
    <div><dt>Scheduled seats with no recorded service</dt><dd>{s.uncoveredSeatCount}</dd></div>
    <div><dt>Shifts missing a recorded start or end</dt><dd>{s.missingBoundaryCount}</dd></div>
    <div><dt>Service exceptions noted</dt><dd>{s.exceptionCount}</dd></div>
    <div><dt>Evidence captured</dt><dd>{report.mediaTotals === null ? "Not included" : mediaSentence(report.mediaTotals)}</dd></div>
  </dl>;
}

// D-07 metadata band. Labels only, no counts and no identifiers, so a reader
// holding one sheet can tell which include set produced it and what window it
// covers without cross-referencing anything.
function Metadata({ report }: { report: DutyActivityReport }) {
  const includes = ["Coverage summary", "Checkpoint exceptions", "Incidents"];
  if (report.mediaTotals !== null) includes.push("Evidence counts");
  return <dl className="pos-paper-metadata">
    <div><dt>Window:</dt><dd>Complete approved service week, {report.header.timeZone}</dd></div>
    <div><dt>Window capped:</dt><dd>No</dd></div>
    <div><dt>Included:</dt><dd>{includes.join(", ")}</dd></div>
    <div><dt>Not included:</dt><dd>{[
      ...(report.mediaTotals === null ? ["Evidence counts"] : []), "Patrol rounds", "Per-seat coverage table",
    ].join(", ")}</dd></div>
    <div><dt>Data through:</dt><dd>{instant(report.header.dataThroughAt, report.header.timeZone)}</dd></div>
    <div><dt>Exported:</dt><dd>{instant(report.header.generatedAt, report.header.timeZone)}</dd></div>
  </dl>;
}

function Notes({ report }: { report: DutyActivityReport }) {
  return <>
    <p className="pos-paper-coverage-note">{dutyActivityReportCopy.coverageElsewhere}</p>
    <p className="pos-paper-coverage-note">{dutyActivityReportCopy.laterRounds}</p>
    {report.mediaTotals !== null && <p className="pos-paper-coverage-note">{dutyActivityReportCopy.evidenceAsOf}</p>}
    <p className="pos-paper-records-note">{dutyActivityReportCopy.recordsNote}</p>
    <Metadata report={report} />
  </>;
}

function Row({ row, timeZone, includeMedia }: { row: SheetRow; timeZone: string; includeMedia: boolean }) {
  if (row.section === "checkpoints") {
    if (row.checkpoint === null) return <td colSpan={checkpointHeadings.length}>No checkpoint exceptions were recorded in the complete approved week.</td>;
    const item = row.checkpoint;
    return <>
      <td>{item.routeLabel}</td>
      <td>{item.checkpointLabel}</td>
      <td>{item.recordedAt === null ? "- missing" : instant(item.recordedAt, timeZone)}</td>
      <td>{serviceCheckpointCopy(item)}</td>
    </>;
  }
  if (row.incident === null) return <td colSpan={incidentHeadings.length + (includeMedia ? 1 : 0)}>No incidents were recorded in the complete approved week.</td>;
  const item = row.incident;
  return <>
    <td>{instant(item.occurredAt, timeZone)}</td>
    <td>{item.postLabel ?? "Site"}</td>
    <td>{category[item.category]}</td>
    <td>{severity[item.severity]}</td>
    <td>{reviewState[item.reviewState]}</td>
    <td>{attribution[item.linkage]}</td>
    {item.mediaCounts === null ? null : <td>{mediaSentence(item.mediaCounts)}</td>}
  </>;
}

function SectionHead({ section, headings, continued = false }: { section: DutySection; headings: string[]; continued?: boolean }) {
  return <thead>
    <tr><th className="pos-paper-section-title" colSpan={headings.length}>{sectionNames[section]}{continued ? " (continued)" : ""}</th></tr>
    <tr>{headings.map((heading) => <th scope="col" key={heading}>{heading}</th>)}</tr>
  </thead>;
}

/** Receives only the validated printable report, never its control revision or any host key. */
export function DutyActivityReportSheet({ report, onReadyChange }: { report: DutyActivityReport; onReadyChange: (ready: boolean) => void }) {
  const root = useRef<HTMLDivElement>(null);
  const readyCallback = useRef(onReadyChange);
  readyCallback.current = onReadyChange;
  const [layout, setLayout] = useState<{ report: DutyActivityReport; pages: ArtifactPage<DutySection>[] | null } | null>(null);
  const includeMedia = report.mediaTotals !== null;
  const headings = useMemo<Record<DutySection, string[]>>(() => ({
    incidents: includeMedia ? [...incidentHeadings, "Evidence captured"] : incidentHeadings,
    checkpoints: checkpointHeadings,
  }), [includeMedia]);
  const rows = useMemo<SheetRow[]>(() => {
    const result: SheetRow[] = [];
    if (report.incidents.length === 0) result.push({ section: "incidents", incident: null });
    else report.incidents.forEach((incident) => result.push({ section: "incidents", incident }));
    if (report.checkpointExceptions.length === 0) result.push({ section: "checkpoints", checkpoint: null });
    else report.checkpointExceptions.forEach((checkpoint) => result.push({ section: "checkpoints", checkpoint }));
    return result;
  }, [report]);
  const pages = layout?.report === report ? layout.pages : null;

  useEffect(() => {
    let cancelled = false;
    readyCallback.current(false);
    const measure = () => {
      if (cancelled || !root.current) return;
      const measurement = root.current.querySelector<HTMLElement>(".pos-sheet-measure");
      if (!measurement) return;
      const headerHeight = measurement.querySelector(".pos-paper-header")?.getBoundingClientRect().height ?? 0;
      const footerHeight = measurement.querySelector(".pos-paper-footer")?.getBoundingClientRect().height ?? 0;
      const headSizes = {} as Record<DutySection, number>;
      sections.forEach((section) => { headSizes[section] = measurement.querySelector(`[data-section="${section}"] thead`)?.getBoundingClientRect().height ?? 0; });
      const measured = Array.from(measurement.querySelectorAll<HTMLElement>("tbody tr"), (node, index) => ({ section: rows[index]!.section, height: Math.ceil(node.getBoundingClientRect().height) + 1 }));
      // Same paper budget as the proof of service: 80px padding, 24px gaps, 12px rounding reserve.
      const packed = headerHeight > 0 && footerHeight > 0 ? paginateArtifactRows<DutySection>(measured, headSizes, 1056 - 80 - 24 - 12 - headerHeight - footerHeight) : null;
      setLayout({ report, pages: packed });
    };
    void (document.fonts?.ready ?? Promise.resolve()).then(measure);
    return () => { cancelled = true; readyCallback.current(false); };
  }, [report, rows]);

  useLayoutEffect(() => {
    if (!pages || !root.current) return;
    const pageNodes = Array.from(root.current.querySelectorAll<HTMLElement>(".pos-paper:not(.pos-sheet-measure)"));
    const fits = pageNodes.length === pages.length && pageNodes.every((node) => {
      const content = node.querySelector(".pos-paper-content")?.getBoundingClientRect();
      const footer = node.querySelector(".pos-paper-footer")?.getBoundingClientRect();
      return content && footer && content.height > 0 && content.bottom <= footer.top - 8;
    });
    readyCallback.current(fits);
  }, [pages]);

  const timeZone = report.header.timeZone;
  const approvedAt = report.approval.receipt.approvedAt;
  return <div className="pos-sheet" ref={root}>
    <div className="pos-paper pos-sheet-measure" aria-hidden="true">
      <ClientArtifactHeader title={dutyActivityReportCopy.title} header={report.header} approvedAt={approvedAt}><Summary report={report} /></ClientArtifactHeader>
      {sections.map((section) => <table className={`pos-paper-table pos-paper-table--${section}`} data-section={section} key={section}>
        <SectionHead section={section} headings={headings[section]} />
        <tbody>{rows.map((row, index) => row.section === section ? <tr key={index}><Row row={row} timeZone={timeZone} includeMedia={includeMedia} /></tr> : null)}</tbody>
      </table>)}
      <ClientArtifactFooter header={report.header} page={999} total={999}><Notes report={report} /></ClientArtifactFooter>
    </div>
    {pages === null
      ? <p className="pos-sheet-layout-state" role="status">{layout?.report === report ? dutyActivityReportCopy.unavailable : "Preparing the printable pages..."}</p>
      : pages.map((page, pageIndex) => <ClientArtifactPage key={pageIndex} title={dutyActivityReportCopy.title} header={report.header} approvedAt={approvedAt}
        summary={<Summary report={report} />} pagination={{ page: pageIndex + 1, total: pages.length }} recordsNote={<Notes report={report} />}>
        {page.groups.map((group, groupIndex) => <table className={`pos-paper-table pos-paper-table--${group.section}`} key={groupIndex}>
          <SectionHead section={group.section} headings={headings[group.section]} continued={group.continued} />
          <tbody>{group.rowIndexes.map((index) => <tr key={index}><Row row={rows[index]!} timeZone={timeZone} includeMedia={includeMedia} /></tr>)}</tbody>
        </table>)}
      </ClientArtifactPage>)}
  </div>;
}
